import { trigger } from '@angular/animations';
import { NgFor, NgIf } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import {
  GalleryDto,
  GalleryFeedbackOption,
  GalleryPresetDto,
  GalleryPublicAccessLevel,
  TenantDto,
  UpdateGalleryBodyDto,
  WatermarkDto,
} from '@picflow/types';
import { Subscription, firstValueFrom } from 'rxjs';
import { ShareGalleryDialogComponent } from 'src/app/account/dialogs/share-gallery-dialog/share-gallery-dialog.component';
import { ShareSubGalleryDialogComponent } from 'src/app/account/dialogs/share-sub-gallery-dialog/share-sub-gallery-dialog.component';
import { maxHeightIf } from 'src/app/animations/max-height.if.animation';
import { allFeedbackOptions, downloadSizes, maxWatermarkApplyTime } from 'src/app/consts';
import { DialogOptions } from 'src/app/interfaces/dialog-data.interface';
import { arraysEqualNoOrder } from 'src/app/lib/arrays-equal';
import { capitalize } from 'src/app/lib/capitalize';
import { convertGalleryDownloadSize } from 'src/app/lib/convert-gallery-download-size';
import { debounce } from 'src/app/lib/debounce';
import { getFormControl } from 'src/app/lib/get-form-control';
import { loadFont } from 'src/app/lib/load-font';
import { getPresetChanges } from 'src/app/lib/new-gallery';
import { objectsMatch } from 'src/app/lib/objects-match';
import { sleep } from 'src/app/lib/sleep';
import { uppercase } from 'src/app/lib/uppercase';
import { AccessLevelService } from 'src/app/services/access-level.service';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { AssetService } from 'src/app/services/asset.service';
import { DialogService, OnGetData } from 'src/app/services/dialog.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { GalleryMetaImageUploadService } from 'src/app/services/gallery-meta-image-upload.service';
import { GalleryPresetService } from 'src/app/services/gallery-preset.service';
import { GalleryService } from 'src/app/services/gallery.service';
import { NewFeatureDotService } from 'src/app/services/new-feature-dot.service';
import { PermissionService } from 'src/app/services/permission.service';
import { SectionService } from 'src/app/services/section.service';
import { ShareService } from 'src/app/services/share.service';
import { StateService } from 'src/app/services/state.service';
import { TenantService } from 'src/app/services/tenant.service';
import { ToastService } from 'src/app/services/toast.service';
import { UploadService } from 'src/app/services/upload.service';
import { WatermarkService } from 'src/app/services/watermark.service';
import { StickyFooterItem } from 'src/app/shared-extra/components/sticky-footer/sticky-footer.component';
import { ActionBoxButtonComponent } from '../../../account-gallery-shared/components/action-box/action-box-button/action-box-button.component';
import { ActionBoxContentComponent } from '../../../account-gallery-shared/components/action-box/action-box-content/action-box-content.component';
import { ActionBoxIconComponent } from '../../../account-gallery-shared/components/action-box/action-box-icon/action-box-icon.component';
import { ActionBoxSwitchComponent } from '../../../account-gallery-shared/components/action-box/action-box-switch/action-box-switch.component';
import { ActionBoxComponent } from '../../../account-gallery-shared/components/action-box/action-box.component';
import { NewFeatureDotComponent } from '../../../account-gallery-shared/components/new-feature-dot/new-feature-dot.component';
import { NewFeatureDirective } from '../../../account-gallery-shared/directives/new-feature.directive';
import { IsDarkColorPipe } from '../../../color-picker/is-dark-color.pipe';
import { ColorSelectorComponent } from '../../../color-selector/color-selector.component';
import { InputFieldComponent } from '../../../shared-extra/components/input-field/input-field.component';
import { ProTagComponent } from '../../../shared-extra/components/pro-tag/pro-tag.component';
import { RadioButtonComponent } from '../../../shared-extra/components/radio-button/radio-button.component';
import { StickyFooterComponent } from '../../../shared-extra/components/sticky-footer/sticky-footer.component';
import { InputPostDirective } from '../../../shared-extra/directives/input-post.directive';
import { InputPreDirective } from '../../../shared-extra/directives/input-pre.directive';
import { PermissionsPipe } from '../../../shared-extra/pipes/permissions.pipe';
import { ButtonMenuItemComponent } from '../../../shared/components/button-menu-item/button-menu-item.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { ResizeDirective } from '../../../shared/directives/resize.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { TransitionPreventorDirective } from '../../../shared/directives/transition-preventor.directive';
import { CapitalizePipe } from '../../../shared/pipes/capitalize.pipe';
import { GalleryTeaserUrlPipe } from '../../../shared/pipes/gallery-teaser-url.pipe';
import { FontSelectionMenuComponent } from '../../components/font-selection-menu/font-selection-menu.component';
import { GallerySettingsItemComponent } from '../../components/gallery-settings-item/gallery-settings-item.component';
import { GallerySettingsPresetsContextComponent } from '../../components/gallery-settings-presets-context/gallery-settings-presets-context.component';
import { GallerySettingsItem } from '../../types/gallery-settings-item.type';
import { DeleteGalleryDialogComponent } from '../delete-gallery-dialog/delete-gallery-dialog.component';
import { SaveGalleryPresetDialogComponent } from '../save-gallery-preset-dialog/save-gallery-preset-dialog.component';

@Component({
  selector: 'app-gallery-settings-dialog',
  templateUrl: './gallery-settings-dialog.component.html',
  styleUrls: ['./gallery-settings-dialog.component.scss'],
  animations: [trigger('passwordForm', maxHeightIf(72)), trigger('approvalLimitForm', maxHeightIf(72))],
  standalone: true,
  imports: [
    NgIf,
    ResizeDirective,
    ButtonDirective,
    SvgComponent,
    NewFeatureDirective,
    NewFeatureDotComponent,
    ContextMenuComponent,
    TooltipDirective,
    GallerySettingsPresetsContextComponent,
    GallerySettingsItemComponent,
    ActionBoxComponent,
    ActionBoxIconComponent,
    ActionBoxContentComponent,
    ActionBoxSwitchComponent,
    RouterLink,
    NgFor,
    ButtonMenuItemComponent,
    LoadingIndicatorComponent,
    TransitionPreventorDirective,
    FormsModule,
    ProTagComponent,
    ReactiveFormsModule,
    InputFieldComponent,
    InputPreDirective,
    InputPostDirective,
    ActionBoxButtonComponent,
    RadioButtonComponent,
    StickyFooterComponent,
    CapitalizePipe,
    PermissionsPipe,
    GalleryTeaserUrlPipe,
    ColorSelectorComponent,
    IsDarkColorPipe,
    FontSelectionMenuComponent,
  ],
})
export class GallerySettingsDialogComponent implements OnGetData, OnDestroy {
  dialogName = 'gallery-settings';
  mode = 'access';
  gallery: GalleryDto;
  userIsWatcher: boolean;
  close: (...args) => void;
  colors = ['', 'red', 'green'];
  passwordChangable = true;
  passwordForm = new FormGroup({ galleryPassword: getFormControl('gallery-password') });
  approvalLimitForm = new FormGroup({ limit: getFormControl('approval-limit'), softLimit: new FormControl(false) });
  oldPassword: string;
  approvalLimitFormWatcher: Subscription;
  editingGalleryPath = false;
  galleryPathForm = new FormGroup({ galleryPath: getFormControl('galleryPath') });
  changingPath = false;
  customColorLabels = false;
  customLabelsForm = new FormGroup({});
  currentPreset: GalleryPresetDto;
  standardPresets: GalleryPresetDto[] = [];
  customPresets: GalleryPresetDto[] = [];
  watermarks: WatermarkDto[] = [];
  tenant: TenantDto;
  isWatermarkApplying: boolean;
  destroyed = false;

  menuItems: StickyFooterItem[] = [
    {
      key: 'access',
      icon: 'gear',
      text: 'General',
    },
    {
      key: 'review',
      icon: 'bolt',
      text: 'Review',
      newDot: 'gallery-settings-review',
    },
    {
      key: 'download',
      icon: 'down-circle',
      text: 'Download',
    },
  ];

  settingItems: {
    [propName: string]: {
      [itemName: string]: GallerySettingsItem;
    };
  } = {
    access: {
      visibility: {
        icon: '',
        title: '',
        description: '',
        valueOptions: [
          { value: 'public', text: 'Public', icon: 'globe', color: 'simple-green', iconColor: 'green' },
          { value: 'unlisted', text: 'Unlisted', icon: 'link', color: 'simple-primary', iconColor: 'primary' },
          { value: 'private', text: 'Private', icon: 'stop', color: 'simple-warn', iconColor: 'warn' },
        ],
        onChange: this.updateVisibility.bind(this),
      },
      password: {
        icon: 'lock',
        title: 'Password',
        description: 'Set a password for this gallery.',
        onChange: this.updatePasswordToggle.bind(this),
        permission: 'gallery.password',
        overridePermissionIfTrue: true,
      },
      showInProfile: {
        icon: 'browser-picflow',
        title: 'Show on Profile',
        description: '',
        onChange: this.updateShowInProfile.bind(this),
      },
      identifyVisitors: {
        icon: 'contact-card',
        title: 'Identify Visitors',
        description: 'Require name and email to access this gallery.',
        permission: 'gallery.forceIdentification',
        onChange: this.updateRequiresIdentification.bind(this),
      },
      clientAdvancedMode: {
        title: 'Advanced Mode',
        description: 'Let visitors access the advanced mode.',
        icon: 'advanced-mode',
        onChange: this.saveClientAdvancedMode.bind(this),
        permission: 'gallery.clientAdvancedMode',
      },
      metadata: {
        icon: 'image-info',
        title: 'Metadata',
        description: 'Display camera and EXIF information.',
        onChange: this.saveShowMetadata.bind(this),
      },
      versionHistory: {
        icon: 'image-history',
        title: 'Version History',
        description: 'Let clients access older versions of assets.',
        onChange: this.saveVersionHistory.bind(this),
        permission: 'asset.assetVersions',
      },
      watermarking: {
        icon: 'image-watermark',
        title: 'Watermark',
        description: 'Protect your assets with a watermark.',
        permission: 'asset.watermarking',
      },
    },
    review: {
      favourite: {
        icon: 'heart-image',
        title: 'Favorites',
        description: 'Allow visitors to favorite assets.',
        onChange: this.saveFeedbackOptions.bind(this),
        permission: 'asset.favourites',
        value: false,
      },
      flag: {
        icon: 'flags',
        title: 'Flags',
        description: 'Let visitors flag assets as approved or rejected.',
        onChange: this.saveFeedbackOptions.bind(this),
        permission: 'asset.status',
        value: false,
      },
      approvalLimit: {
        icon: 'number',
        title: 'Approval Limit',
        description: 'Define how many assets can be marked as approved.',
        comingSoon: 'approval-limit',
      },
      color_label: {
        icon: 'label',
        title: 'Color Labels',
        description: 'Let visitors mark assets with a color label.',
        onChange: this.saveFeedbackOptions.bind(this),
        permission: 'asset.colorLabels',
        value: false,
      },
      comment: {
        icon: 'comments',
        title: 'Comments',
        description: 'Allow visitors to comment on assets.',
        onChange: this.saveFeedbackOptions.bind(this),
        permission: 'asset.comments',
        value: false,
      },
      annotation: {
        icon: 'annotation',
        title: 'Annotations',
        description: 'Allow clients to use asset annotations tools.',
        onChange: this.saveFeedbackOptions.bind(this),
        permission: 'asset.annotations',
        value: false,
      },
    },
    design: {
      galleryBackground: {
        icon: '',
        title: 'Gallery Background',
        description: '',
      },
      titleFont: {
        icon: '',
        title: 'Titles',
        description: '',
      },
      paragraphFont: {
        icon: '',
        title: 'Paragraph',
        description: '',
      },
    },
    download: {
      singleAsset: {
        icon: 'image-download',
        title: 'Asset Download',
        description: 'Allow individual asset downloads.',
        onChange: this.updateAllowDownload.bind(this),
        permission: 'asset.download',
        value: false,
      },
      fullGallery: {
        icon: 'image-stack-download',
        title: 'Gallery Download',
        description: 'Allow ZIP downloads with all assets.',
        onChange: this.updateAllowDownload.bind(this),
        permission: 'asset.download',
        value: false,
      },
      downloadSize: {
        icon: 'size',
        title: 'Asset Size',
        description: 'Select downloadable asset sizes.',
      },
    },
  };

  allowedFeedbackOptions: GalleryFeedbackOption[] = ['favourite', 'flag', 'color_label', 'comment', 'annotation'];
  enabledDownloadSizes: { [key: string]: boolean };
  downloadSizeString: string;
  selectedWatermark: WatermarkDto;
  downloadSizes = downloadSizes.filter(s => s.name !== 'reserved');
  galleryGuest: boolean;
  updateSub: Subscription;
  publicAccessLevel: GalleryPublicAccessLevel;

  constructor(
    private dialog: DialogService,
    private galleryService: GalleryService,
    public state: StateService,
    private router: Router,
    public featureFlags: FeatureFlagsService,
    private uploadService: UploadService,
    private analytics: AnalyticsService,
    private toasts: ToastService,
    private tenantService: TenantService,
    private galleryPresetService: GalleryPresetService,
    private permissionService: PermissionService,
    private shareService: ShareService,
    private newFeature: NewFeatureDotService,
    private accessLevelService: AccessLevelService,
    private watermarkService: WatermarkService,
    private assetService: AssetService,
    private sectionService: SectionService,
    private galleryMetaImageUploadService: GalleryMetaImageUploadService
  ) {}

  dialogOptions(): DialogOptions {
    return { size: 940, spacingX: 0, spacingY: 0 };
  }

  async onGetData(data: { gallery: GalleryDto; defaultMode: string }, close: (...args) => void) {
    // if (this.featureFlags.get('newStylingOptions')) {
    this.menuItems.splice(2, 0, {
      key: 'design',
      icon: 'annotate-brush',
      text: 'Design',
    });
    // }
    if (this.featureFlags.get('newPermissions')) {
      this.settingItems.access.identifyVisitors.permission = 'gallery.forceIdentification';
    }
    if (!data.gallery) {
      console.log('gallery settings started without gallery');
      return close();
    }
    this.updateSub = this.galleryService.onUpdates.subscribe(async event => {
      if (!event) return;
      if (!this.gallery) return;
      if (event[1] !== this.gallery.id) return;
      if (!event[2]) return; // we only want external updates
      if (event[0] === 'deleted') this.close();
      if (event[0] === 'updated') {
        const newGallery = await this.galleryService.checkForExternalUpdate(this.gallery, event[2]);
        if (newGallery) {
          delete newGallery['tenant'];
          Object.assign(this.gallery, newGallery);
          this.gallery = newGallery;
          this.setFeedbackSettings();
          this.findCurrentPreset();
        }
      }
    });

    // if (this.featureFlags.get('approvalLimit')) {
    //   this.settingItems.review.approvalLimit = {
    //     title: 'Approval Limit',
    //     description: 'Define how many assets should be flagged as approved',
    //     icon: 'number',
    //     permission: 'asset.approvalLimit',
    //     onChange: this.updateApprovalLimit.bind(this),
    //   };
    //   this.approvalLimitFormWatcher = this.approvalLimitForm.valueChanges.subscribe(() => {
    //     this.updateApprovalLimit();
    //   });
    // }

    this.close = close;
    this.gallery = data.gallery;

    if (this.gallery['tenant']?.id) {
      this.tenant = this.gallery['tenant'];
    } else {
      this.galleryService.getGallery(this.gallery.id, { tenant: true }).then(gallery => {
        this.tenant = gallery['tenant'];
      });
    }

    if (data.defaultMode) {
      this.mode = data.defaultMode;
      this.newFeature.markFeatureAsSeen(`gallery-settings-${this.mode}`);
    }
    this.oldPassword = this.gallery['password'];
    this.setFeedbackSettings();

    const [accessLevels, isGalleryGuest] = await Promise.all([
      this.accessLevelService.getAccessLevels(this.gallery),
      firstValueFrom(this.accessLevelService.isGalleryGuest(this.gallery)),
    ]);
    this.publicAccessLevel = accessLevels[1];

    this.galleryGuest = isGalleryGuest;
    if (!this.galleryGuest) {
      const [presets, watermarks] = await Promise.all([
        this.galleryPresetService.getGalleryPresets(),
        this.watermarkService.getWatermarks(),
      ]);
      this.watermarks = watermarks;
      this.standardPresets = presets.filter(p => p.type === 'standard');
      this.customPresets = presets.filter(p => p.type === 'custom');
      this.findCurrentPreset();
      this.setFeedbackSettings();
    }

    this.isWatermarkApplying = await this.checkIsApplyingWatermark();
    if (this.isWatermarkApplying) {
      await this.waitTillNotApplying();
      this.isWatermarkApplying = false;
    }
  }

  private findCurrentPreset() {
    this.currentPreset = [...this.standardPresets, ...this.customPresets].find(preset => {
      if (!arraysEqualNoOrder(preset.enabled_feedback_options, this.gallery.enabled_feedback_options)) return false;
      const changes = getPresetChanges(preset);
      delete changes.enabled_feedback_options;
      const all = Object.keys(changes).every(key => {
        if (key === 'color_labels') {
          const test = arraysEqualNoOrder(
            this.gallery['color_labels'].map(l => l.active + l.name + l.color),
            changes['color_labels'].map(l => l.active + l.name + l.color)
          );
          return test;
        }

        if (
          this.gallery[key] !== null &&
          changes[key] !== null &&
          (typeof this.gallery[key] === 'object' || typeof changes[key] === 'object')
        ) {
          return objectsMatch(this.gallery[key], changes[key]);
        }

        return this.gallery[key] === changes[key];
      });
      return all;
    });
  }

  async openParentGallerySettingsDialog() {
    const gallery = await this.galleryService.getGallery(this.gallery['parent_gallery_id']);
    this.dialog.open(GallerySettingsDialogComponent, { gallery });
  }

  // updateApprovalLimit() {
  //   if (!this.featureFlags.get('approvalLimit')) return;
  //   const origValue = this.gallery.approval_limits;
  //   if (this.settingItems.review.approvalLimit.value) {
  //     if (!this.gallery.approval_limits && !this.approvalLimitForm.value.limit) {
  //       this.gallery.approval_limits = <number>(<unknown>'');
  //       this.approvalLimitForm.setValue(
  //         {
  //           limit: this.gallery.approval_limits,
  //           softLimit: false,
  //         },
  //         { emitEvent: false }
  //       );
  //     } else {
  //       let v = this.approvalLimitForm.value.limit;
  //       if (v === '') v = 0;
  //       this.gallery.approval_limits = parseInt(v);
  //     }
  //     if (!this.approvalLimitForm.valid) return;
  //     if (this.approvalLimitForm.value.softLimit && this.gallery.approval_limits > 0) {
  //       // hard limit and soft limit are determined by using positive and negative numbers
  //       this.gallery.approval_limits = -this.gallery.approval_limits;
  //     }
  //   } else {
  //     this.gallery.approval_limits = null;
  //   }
  //   if (origValue === this.gallery.approval_limits) return;
  //   this.updateGallery({ approval_limits: this.gallery.approval_limits });
  // }

  async saveFeedbackOptions() {
    this.gallery.enabled_feedback_options = this.getFeedbackOptions();
    await this.updateGallery({ enabled_feedback_options: this.gallery.enabled_feedback_options });
    this.setFeedbackSettings();
  }

  async updateBackgroundColor(color: string) {
    this.gallery.layout_background_color = color;
    this.settingItems.design.galleryBackground.value = color;
    this.findCurrentPreset();
    this.saveBackgroundcolor();
    this.galleryService.galleryUpdateTimeMap.set(this.gallery.id, Date.now());
  }

  saveBackgroundcolor = debounce(() => {
    this.galleryService.updateGallery(this.gallery.id, {
      layout_background_color: this.gallery.layout_background_color || null,
    });
  }, 500);

  async updateTitleFontFamily(font: string) {
    this.gallery.default_title_typography.font_family = font;
    loadFont(font);
    this.findCurrentPreset();
    await this.updateGallery({ default_title_typography: this.gallery.default_title_typography });
  }

  async updateParagraphFontFamily(font: string) {
    this.gallery.default_paragraph_typography.font_family = font;
    loadFont(font);
    this.findCurrentPreset();
    await this.updateGallery({ default_paragraph_typography: this.gallery.default_paragraph_typography });
  }

  updateTitleFontColor(color: string) {
    this.gallery.default_title_typography.font_color = color;
    this.findCurrentPreset();
    this.saveFontColors();
    this.galleryService.galleryUpdateTimeMap.set(this.gallery.id, Date.now());
  }

  updateParagraphFontColor(color: string) {
    this.gallery.default_paragraph_typography.font_color = color;
    this.findCurrentPreset();
    this.saveFontColors();
    this.galleryService.galleryUpdateTimeMap.set(this.gallery.id, Date.now());
  }

  saveFontColors = debounce(() => {
    this.updateGallery({
      default_title_typography: this.gallery.default_title_typography,
      default_paragraph_typography: this.gallery.default_paragraph_typography,
    });
  }, 500);

  saveDownloadSize(size: number) {
    this.gallery.download_media_size = size;
    this.updateGallery({ download_media_size: size });
  }

  saveClientAdvancedMode() {
    this.gallery.client_advanced_mode_enabled = !!this.settingItems.access.clientAdvancedMode?.value;
    this.updateGallery({
      client_advanced_mode_enabled: this.gallery.client_advanced_mode_enabled,
    });
  }

  saveShowMetadata() {
    this.gallery.save_camera_metadata = !!this.settingItems.access.metadata?.value;
    this.updateGallery({
      save_camera_metadata: this.gallery.save_camera_metadata,
    });
  }

  saveVersionHistory() {
    this.gallery.version_history_enabled = !!this.settingItems.access.versionHistory?.value;
    this.updateGallery({ version_history_enabled: this.gallery.version_history_enabled });
  }

  async applyWatermark(watermark: WatermarkDto) {
    this.selectedWatermark = watermark;
    this.gallery.watermark = watermark?.id || null;
    if (watermark) this.isWatermarkApplying = true;
    await this.updateGallery({ watermark: this.gallery.watermark });
    if (watermark) {
      this.close();
      this.toasts.add('Applying watermarks', 'success');
      await sleep(1000);
      this.isWatermarkApplying = false;
    }
  }

  setWatermarking(value: boolean) {
    this.settingItems.access.watermarking.value = value;
    if (!value && this.gallery.watermark) {
      this.applyWatermark(undefined);
    }
  }

  updateAllowDownload() {
    this.gallery.download_single_asset = !!this.settingItems.download.singleAsset.value;
    this.gallery.download_full_gallery = !!this.settingItems.download.fullGallery.value;

    this.updateGallery({
      download_full_gallery: this.gallery.download_full_gallery,
      download_single_asset: this.gallery.download_single_asset,
    });
  }

  private calcDownloadSizeString() {
    const enabledCount = Object.values(this.enabledDownloadSizes)
      .map(a => (a ? 1 : 0))
      .reduce((t, v) => t + v, 0);
    const maxCount = this.downloadSizes.length;
    if (enabledCount >= maxCount) return 'All Sizes';
    if (enabledCount > 1) return `${enabledCount} Sizes`;
    const enabled = Object.keys(this.enabledDownloadSizes).find(size => this.enabledDownloadSizes[size]);
    return capitalize(enabled);
  }

  calcEnabledDownloadSizes() {
    this.enabledDownloadSizes = convertGalleryDownloadSize(this.gallery.download_media_size);
    this.downloadSizeString = this.calcDownloadSizeString();
  }

  private getEnabledDownloadSizesCount() {
    return Object.values(this.enabledDownloadSizes)
      .map(a => (a ? 1 : 0))
      .reduce((t, v) => t + v, 0);
  }

  toggleDownloadSize(size: string) {
    if (this.enabledDownloadSizes[size] && this.getEnabledDownloadSizesCount() === 1) return;
    this.enabledDownloadSizes[size] = !this.enabledDownloadSizes[size];
    const downloadSizesString = downloadSizes.map(s => (this.enabledDownloadSizes[s.name] ? '1' : '0')).join('');
    this.gallery.download_media_size = parseInt(downloadSizesString, 2);
    const maxSize = Math.pow(2, downloadSizes.filter(d => d.name !== 'reserved').length) - 1;
    if (this.gallery.download_media_size === maxSize) this.gallery.download_media_size = 999999;
    this.updateGallery({ download_media_size: this.gallery.download_media_size });
    this.calcEnabledDownloadSizes();
  }

  updatePrimaryColor() {
    this.updateGallery({ primary_color: this.gallery.primary_color });
  }

  updateSaveCameraMetadata() {
    this.updateGallery({ save_camera_metadata: this.gallery.save_camera_metadata });
  }

  saveCustomLabels = debounce(async () => {
    Object.keys(this.customLabelsForm.value).forEach(key => {
      const label = this.gallery.color_labels.find(l => l.color === key);
      if (!label) return;
      label.name = this.customLabelsForm.value[key].slice(0, 16) || capitalize(label.color);
    });

    await this.updateGallery({ color_labels: this.gallery.color_labels });
  }, 1000);

  async onToggleCustomLabels() {
    if (!this.customColorLabels) {
      this.resetColorLabels();
      this.setFeedbackSettings();
      await this.updateGallery({ color_labels: this.gallery.color_labels });
      this.analytics.track('disabled_custom_labels');
    } else {
      this.analytics.track('enabled_custom_labels');
    }
  }

  private resetColorLabels() {
    this.gallery.color_labels = [
      { color: 'red', name: 'Red', active: true },
      { color: 'orange', name: 'Orange', active: true },
      { color: 'blue', name: 'Blue', active: true },
      { color: 'purple', name: 'Purple', active: true },
      { color: 'green', name: 'Green', active: true },
    ];
  }

  async deleteGallery(gallery: GalleryDto) {
    const resp = await this.dialog.open(DeleteGalleryDialogComponent, { gallery });
    if (resp) {
      this.router.navigate(['a']);
      this.close(undefined);
    }
  }

  getGalleryColorName(color: string) {
    if (!color) return 'Default';
    return color.charAt(0).toUpperCase() + color.slice(1);
  }

  private getFeedbackOptions(): GalleryFeedbackOption[] {
    const options = [];
    this.allowedFeedbackOptions.forEach(option => this.settingItems.review[option]?.value && options.push(option));
    return options;
  }

  private async setVisibility() {
    const item = this.settingItems.access.visibility;
    this.settingItems.access.showInProfile.value = this.gallery.show_in_profile;

    if (this.gallery.public_access === 'no_access') {
      item.value = 'private';
      item.icon = 'access-private';
      item.title = 'Private';
      item.description = 'Only you can see this gallery.';
    } else if (this.gallery.show_in_profile) {
      item.value = 'public';
      item.icon = 'access-public';
      item.title = 'Public';
      const link = await this.shareService.getShareDomain(this.gallery);
      item.description = `Visible on your <a href="${link}" target="_blank" class="primary-link with-external-link-icon">Picflow profile</a>`;
    } else {
      item.value = 'unlisted';
      item.icon = 'access-unlisted';
      item.title = 'Unlisted';
      item.description = 'Only people with the link can access.';
    }
  }

  private setFeedbackSettings() {
    this.customColorLabels = !!this.gallery.color_labels.find(l => !l.active || l.name !== uppercase(l.color));

    if (!this.permissionService.canDisplayElement('asset.customLabels', this.gallery)) {
      this.customColorLabels = false;
      this.resetColorLabels();
    }

    if (this.gallery['password']) this.passwordForm.controls.galleryPassword.setValue(this.gallery['password']);
    this.settingItems.access.password.value = this.gallery.password_enabled;

    this.setVisibility();
    if (this.gallery['password']) this.disablePasswordField();
    allFeedbackOptions.forEach(key => (this.settingItems.review[key].value = false));
    this.gallery.enabled_feedback_options.forEach(key => (this.settingItems.review[key].value = true));
    this.settingItems.download.singleAsset.value = this.gallery.download_single_asset;
    this.settingItems.download.fullGallery.value = this.gallery.download_full_gallery;
    this.calcEnabledDownloadSizes();
    this.settingItems.access.identifyVisitors.value = this.gallery.requires_user_identification;
    this.settingItems.access.clientAdvancedMode.value = this.gallery.client_advanced_mode_enabled;
    this.settingItems.access.versionHistory.value = this.gallery.version_history_enabled;
    this.settingItems.access.metadata.value = this.gallery.save_camera_metadata;
    if (this.watermarks) {
      const watermark = this.watermarks.find(watermark => watermark.id === this.gallery.watermark);
      this.selectedWatermark = watermark;

      this.settingItems.access.watermarking.value = !!this.gallery.watermark;

      // this.settingItems.access.watermarking.value = watermark?.id || null;
      // this.settingItems.access.watermarking.valueOptions = this.watermarks.map(watermark => {
      //   return {
      //     text: watermark.name,
      //     value: watermark.id,
      //     color: 'simple',
      //   };
      // });
      // this.settingItems.access.watermarking.valueOptions.push({
      //   text: 'None',
      //   value: null,
      //   color: 'simple',
      // });
    }

    // if (this.featureFlags.get('approvalLimit')) {
    //   this.settingItems.review.approvalLimit.value = !!this.gallery.approval_limits;
    //   if (this.settingItems.review.approvalLimit.value) {
    //     if (this.gallery.approval_limits > 0) {
    //       this.approvalLimitForm.setValue({
    //         limit: this.gallery.approval_limits,
    //         softLimit: false,
    //       });
    //     } else {
    //       this.approvalLimitForm.setValue({
    //         limit: -this.gallery.approval_limits,
    //         softLimit: true,
    //       });
    //     }
    //   }
    // }

    this.gallery.color_labels.forEach(label => {
      this.customLabelsForm.removeControl(label.color);
      this.customLabelsForm.addControl(label.color, getFormControl('customLabel', label.name));
    });
  }

  enablePasswordField() {
    this.passwordChangable = true;
    this.passwordForm.controls.galleryPassword.enable();
  }

  disablePasswordField() {
    this.passwordChangable = false;
    this.passwordForm.controls.galleryPassword.disable();
  }

  async updateVisibility() {
    const value = <'public' | 'unlisted' | 'private'>this.settingItems.access.visibility.value;

    if (value === 'public') {
      this.gallery.show_in_profile = true;
      this.gallery.public_access = 'review';
    } else if (value === 'unlisted') {
      this.gallery.show_in_profile = false;
      this.gallery.public_access = 'review';
    } else {
      this.gallery.show_in_profile = false;
      this.gallery.public_access = 'no_access';
    }

    this.setVisibility();

    const params: UpdateGalleryBodyDto = {
      show_in_profile: this.gallery.show_in_profile,
      public_access: this.gallery.public_access,
    };

    await this.updateGallery(params);
    this.analytics.track('gallery_visibility_updated');
  }

  async updateShowInProfile() {
    const value = <boolean>this.settingItems.access.showInProfile.value;
    this.gallery.show_in_profile = value;
    this.setVisibility();
    const params: UpdateGalleryBodyDto = {
      show_in_profile: this.gallery.show_in_profile,
    };
    await this.updateGallery(params);
    this.analytics.track('gallery_in_profile_updated');
  }

  async updatePasswordToggle() {
    const isPassword = this.settingItems.access.password.value && !!this.passwordForm.value.galleryPassword;

    this.gallery.password_enabled = isPassword;

    const params: UpdateGalleryBodyDto = { password_enabled: this.gallery.password_enabled };
    if (isPassword) params.password = this.gallery['password'];
    this.oldPassword = this.gallery['password'];

    await this.updateGallery(params);
    this.analytics.track('gallery_password_updated');
  }

  updateRequiresIdentification() {
    this.gallery.requires_user_identification = !!this.settingItems.access.identifyVisitors.value;
    this.updateGallery({ requires_user_identification: this.gallery.requires_user_identification });
    this.analytics.track('gallery_requires_ident_updated');
  }

  async savePassword() {
    if (!this.passwordChangable) return this.enablePasswordField();
    if (!this.passwordForm.valid) return;
    this.gallery.password_enabled = true;

    await this.updateGallery({
      password: this.passwordForm.value.galleryPassword,
      password_enabled: true,
    });
    if (this.oldPassword !== this.passwordForm.value.galleryPassword) {
      this.analytics.track('gallery_password_updated');
      this.toasts.add('Password saved', 'success');
      this.gallery['password'] = this.passwordForm.value.galleryPassword;
    }
    this.oldPassword = this.gallery['password'];
    this.disablePasswordField();
  }

  galleryPasswordCopied() {
    this.toasts.add('Copied', 'success');
  }

  async uploadTeaserEvent(target: EventTarget) {
    const files = (target as HTMLInputElement).files;
    this.uploadTeaser(files[0]);
  }

  async uploadTeaser(file: File) {
    await this.galleryMetaImageUploadService.upload(file, this.gallery, 'teaser');
  }

  async removeTeaser() {
    this.gallery.teaser.uuid = null;
    await this.updateGallery({ teaser: this.gallery.teaser });
  }

  openShareDialog() {
    if (this.gallery.section) {
      this.dialog.open(ShareSubGalleryDialogComponent, { gallery: this.gallery });
    } else {
      this.dialog.open(ShareGalleryDialogComponent, { gallery: this.gallery });
    }
  }

  applyPreset(preset: GalleryPresetDto) {
    const params = getPresetChanges(preset);
    Object.assign(this.gallery, params);
    this.updateGallery(params);
    this.setFeedbackSettings();
  }

  private async updateGallery(params: UpdateGalleryBodyDto) {
    this.findCurrentPreset();
    const resp = await this.galleryService.updateGallery(this.gallery.id, params);
    return resp;
  }

  public async refreshWatermark() {
    this.galleryService.refreshWatermark(this.gallery.id);
    this.toasts.add('Updating watermarks', 'success');
    this.isWatermarkApplying = true;
    await sleep(1000 * 10); // waiting 10 seconds because we don't know when the watermark has been updated
    this.isWatermarkApplying = true;
  }

  createPreset() {
    if (!this.permissionService.canDisplayElement('gallery.customPresets', this.gallery)) {
      return this.router.navigate(['/a', 'pricing']);
    }
    this.dialog.open(SaveGalleryPresetDialogComponent, { gallery: this.gallery, settingsMode: this.mode });
  }

  downloadSizeDisabled() {
    if (this.settingItems.download.singleAsset.value) return false;
    if (this.settingItems.download.fullGallery.value) return false;

    return true;
  }

  async checkIsApplyingWatermark() {
    if (this.destroyed) return;
    if (!this.gallery.watermark) return;
    if (Date.now() - this.gallery.updated_at * 1000 > maxWatermarkApplyTime) {
      // if the gallery has not been updated in the last 2 minutes, we assume that the watermark has been applied
      // This is a workaround because sometimes the watermark fails for an asset
      return false;
    }
    const assets = await this.assetService.getAssetsForGallery(this.gallery.id);
    const isApplying = assets.some(asset => {
      if (asset.type !== 'image') return false;
      if (!asset.watermark_enabled) return false;
      return !asset.version_data.watermark_uuid;
    });
    return isApplying;
  }

  private async waitTillNotApplying() {
    await sleep(1000 * 5);
    const resp = await this.checkIsApplyingWatermark();
    if (!resp) return;
    return this.waitTillNotApplying();
  }

  async onRemoveOverridesTitle() {
    const cover_title = this.gallery.cover.title_typography;
    if (cover_title?.font_family) {
      cover_title.font_family = null;
      this.galleryService.updateGallery(this.gallery.id, { cover: this.gallery.cover });
    }
    const sections = await this.sectionService.getSections(this.gallery.id);
    await Promise.all([
      ...sections.map(async section => {
        if (section.object !== 'text') return;
        if (!section.text_title_typography?.font_family) return;
        section.text_title_typography.font_family = null;
        await this.sectionService.updateSection(section.id, { text_title_typography: section.text_title_typography });
      }),
    ]);
    this.galleryService.triggerHackyUpdate(this.gallery.id);
  }

  async onRemoveOverridesParagraph() {
    const cover_paragraph = this.gallery.cover.paragraph_typography;
    if (cover_paragraph?.font_family) {
      cover_paragraph.font_family = null;
      this.galleryService.updateGallery(this.gallery.id, { cover: this.gallery.cover });
    }
    this.gallery.cover.paragraph_typography.font_family = null;
    const sections = await this.sectionService.getSections(this.gallery.id);
    await Promise.all([
      ...sections.map(async section => {
        if (section.object !== 'text') return;
        if (!section.text_paragraph_typography?.font_family) return;
        section.text_paragraph_typography.font_family = null;
        await this.sectionService.updateSection(section.id, {
          text_paragraph_typography: section.text_paragraph_typography,
        });
      }),
    ]);
    this.galleryService.triggerHackyUpdate(this.gallery.id);
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.approvalLimitFormWatcher?.unsubscribe();
    this.updateSub?.unsubscribe();
  }
}
